import type { Middleware } from 'openapi-fetch';
import { CSRF_REJECTION_ERROR } from './csrf';
import { isStepUpCeremonyRequest } from './step-up';

/**
 * Detection half of the global permission-denied handler. It announces a 403 on
 * a `sentinel:forbidden` window event and renders nothing — the toast lives in
 * `ForbiddenToastListener` (`@sentinel/auth-core`). The response is always
 * returned unchanged, so every call site still sees its own 403.
 *
 * `createApiClient` registers this middleware FIRST. openapi-fetch runs
 * `onResponse` in reverse registration order, so first-registered runs last:
 * this sees the response only after step-up has replayed the request and after
 * CSRF has spent its one-shot retry. Registered anywhere else, it would announce
 * a 403 that a sibling middleware is about to resolve.
 *
 * `detail.message` carries the backend's raw `error` string. The listener does
 * NOT render it — those are English-only strings like "no org context" and would
 * read badly in a zh-TW UI. It is there because the listener dedupes on it, and
 * because a future per-screen listener would need it. So the body parse below is
 * not dead code.
 */

/** Window event announcing a permission-denied 403. */
export const FORBIDDEN_EVENT = 'sentinel:forbidden';

/**
 * Request header that opts out of the global handler. Set it where the screen
 * renders the 403 itself and a toast would double up on the message.
 */
export const SKIP_FORBIDDEN_HEADER = 'x-sentinel-skip-forbidden';

/**
 * Request header that opts a safe method in. Only for user-initiated one-shot
 * actions that happen to be GETs (the portal data exports); a 403 on a plain GET
 * is a page-load condition the screen renders inline.
 */
export const FORCE_FORBIDDEN_HEADER = 'x-sentinel-force-forbidden';

export interface ForbiddenEventDetail {
  status: 403;
  /** Raw `error` field from the response body, or undefined when the body was not JSON. */
  message: string | undefined;
  method: string;
  url: string;
}

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Backend `error` values that are a 403 in the wire sense but not a refusal the
 * user can act on.
 */
const NOT_A_REFUSAL = new Set([
  // The step-up middleware owns this one, cancel case included. Without the
  // exclusion, dismissing the step-up dialog would raise a permission error.
  'step_up_required',
  // Enrollment routing, not a refusal.
  'mfa_required',
  'mfa_enrollment_required',
  // The CSRF one-shot retry already failed. Also what keeps the 30-second
  // /auth/token refresh from turning into a toast loop. Shared with csrf.ts so
  // the two cannot drift if the backend ever changes the wording.
  CSRF_REJECTION_ERROR,
]);

/**
 * Reads the `error` field off a clone so the caller can still read the body.
 * Returns `undefined` when the body is absent or not JSON — a denial with no
 * JSON body is still a denial.
 */
async function readErrorField(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.clone().json()) as { error?: unknown };
    return typeof body?.error === 'string' ? body.error : undefined;
  } catch {
    return undefined;
  }
}

export function createForbiddenMiddleware(): Middleware {
  return {
    async onResponse({ request, response }) {
      // Only a 403 is a permission denial.
      if (response.status !== 403) return undefined;
      // A 403 on a safe method is a page-load condition the screen renders
      // inline; the force header opts in the few user-initiated GET actions.
      if (
        !MUTATION_METHODS.has(request.method) &&
        request.headers.get(FORCE_FORBIDDEN_HEADER) !== '1'
      ) {
        return undefined;
      }
      // The call site said it renders this 403 itself.
      if (request.headers.get(SKIP_FORBIDDEN_HEADER) === '1') return undefined;
      // A 403 from the step-up ceremony is already rendered inside the dialog.
      if (isStepUpCeremonyRequest(request)) return undefined;
      // Checked before the body read so a non-browser context pays for neither.
      if (typeof window === 'undefined') return undefined;
      const message = await readErrorField(response);
      // Step-up, MFA enrollment and CSRF 403s belong to other middlewares.
      if (message && NOT_A_REFUSAL.has(message)) return undefined;
      const detail: ForbiddenEventDetail = {
        status: 403,
        message,
        method: request.method,
        url: request.url,
      };
      window.dispatchEvent(new CustomEvent<ForbiddenEventDetail>(FORBIDDEN_EVENT, { detail }));
      // The 403 must still reach the caller unchanged.
      return undefined;
    },
  };
}
